<template>
  <div class="main-container" :class="{ 'is-embedded': embedded }">
    <div class="container-archivio-file">
      <div v-if="!embedded" class="header">
        <h2 class="title">ARCHIVIO FILE CONVERTITI</h2>
      </div>

      <div class="skeleton-loader" v-if="isLoading">
        <div class="skeleton skeleton-title"></div>
        <div class="skeleton skeleton-text"></div>
        <div class="skeleton skeleton-button"></div>
      </div>

      <div class="archive-card" v-else>
        <div
          class="filters-row"
          :class="{ 'filters-row--locked': !canAccessArchive }"
        >
          <select
            v-model="selectedOperation"
            class="filter-input"
            :disabled="!canAccessArchive"
          >
            <option value="">Tutti gli strumenti</option>
            <option value="Conversione Prospetto Paghe">Prospetto Paghe</option>
            <option value="Conversione Modello F24">Modello F24</option>
            <option value="Conversione Bilancio Analitico">
              Bilancio Analitico
            </option>
          </select>

          <input
            v-model.trim="searchTerm"
            type="text"
            class="filter-input"
            placeholder="Cerca per nome file"
            :disabled="!canAccessArchive"
          />

          <span v-if="canAccessArchive" class="premium-chip">Premium</span>
        </div>

        <p v-if="archiveMessage" class="archive-message">
          {{ archiveMessage }}
        </p>

        <div
          class="archive-table-shell"
          :class="{ 'archive-table-shell--locked': !canAccessArchive }"
        >
          <div v-if="!canAccessArchive" class="premium-overlay">
            <div class="premium-overlay-card">
              <div class="premium-overlay-badge">Sezione Premium</div>
              <p>Consulta e riapri i file archiviati dello studio.</p>
              <router-link to="/home/store" class="premium-cta">
                Vai allo Store
              </router-link>
            </div>
          </div>

          <div class="archive-table-wrap">
            <table class="archive-table">
              <thead>
                <tr>
                  <th>Strumento</th>
                  <th>File Caricato</th>
                  <th>File Elaborato</th>
                  <th>Data Operazione</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="canAccessArchive && displayedLogs.length === 0">
                  <td colspan="4" class="operationtext">
                    Nessun file archiviato trovato con i filtri selezionati.
                  </td>
                </tr>

                <template v-else>
                  <template v-for="log in displayedLogs">
                    <tr
                      :key="log.id"
                      :class="{ 'row-expanded': isExpanded(log.id) }"
                    >
                      <td class="archive-tool-cell">
                        <OperationToolLabel :operation="log.operazione" />
                      </td>
                      <td class="archive-uploaded-cell">
                        <button
                          v-if="
                            canAccessArchive && hasUploadedArchivedFile(log)
                          "
                          class="archive-link"
                          @click.stop="
                            openOrDownloadArchivedFile(
                              log,
                              0,
                              'uploaded',
                              getUploadedArchivedFileLabel(log)
                            )
                          "
                        >
                          {{ getUploadedArchivedFileLabel(log) }}
                        </button>
                        <span
                          v-else
                          class="archive-link disabled"
                          @click.stop="handleLockedArchiveClick"
                        >
                          {{ getUploadedArchivedFileLabel(log) }}
                        </span>
                      </td>
                      <td
                        @click="
                          canAccessArchive &&
                          log.file_scaricati &&
                          log.file_scaricati.length > 1
                            ? toggleExpand(log.id)
                            : handleLockedArchiveClick()
                        "
                        :class="{
                          'clickable-cell':
                            canAccessArchive &&
                            ((log.file_scaricati &&
                              log.file_scaricati.length > 1) ||
                              hasArchivedFiles(log)),
                        }"
                      >
                        <template v-if="Array.isArray(log.file_scaricati)">
                          <template v-if="log.file_scaricati.length === 1">
                            <button
                              v-if="canAccessArchive && hasArchivedFiles(log)"
                              class="archive-link"
                              @click.stop="
                                openOrDownloadArchivedFile(
                                  log,
                                  0,
                                  'converted',
                                  getArchivedFileLabel(log, 0)
                                )
                              "
                            >
                              {{ getArchivedFileLabel(log, 0) }}
                            </button>
                            <span
                              v-else
                              class="archive-link disabled"
                              @click.stop="handleLockedArchiveClick"
                            >
                              {{ getArchivedFileLabel(log, 0) }}
                            </span>
                          </template>
                          <template v-else-if="log.file_scaricati.length > 1">
                            <span class="chip">
                              <i
                                class="fa-solid fa-folder"
                                aria-hidden="true"
                              ></i>
                              {{ log.file_scaricati.length }} file
                            </span>
                          </template>
                          <template v-else>Nessun file scaricato</template>
                        </template>
                        <template v-else>Nessun file scaricato</template>
                      </td>
                      <td>{{ formatDate(log.download_date) }}</td>
                    </tr>

                    <tr
                      v-if="canAccessArchive && isExpanded(log.id)"
                      :key="'expanded-' + log.id"
                      class="expanded-log-row"
                    >
                      <td colspan="4" class="expanded-row">
                        <span class="filescaricatititle">File elaborati:</span>
                        <ul>
                          <li
                            v-for="(file, index) in log.file_scaricati || []"
                            :key="`${log.id}-${file}-${index}`"
                          >
                            <button
                              v-if="hasArchivedFileAt(log, index)"
                              class="archive-link"
                              @click="
                                openOrDownloadArchivedFile(
                                  log,
                                  index,
                                  'converted',
                                  getArchivedFileLabel(log, index)
                                )
                              "
                            >
                              {{ getArchivedFileLabel(log, index) }}
                            </button>
                            <template v-else>{{ file }}</template>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/config.js";
import { haFunzionalita as ha } from "@/utils/plans";
import OperationToolLabel from "@/components/OperationToolLabel.vue";

export default {
  name: "ArchivioFileConvertiti",
  components: { OperationToolLabel },
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      isLoading: true,
      logs: [],
      selectedOperation: "",
      searchTerm: "",
      expandedRows: [],
      archiveMessage: "",
      archiveMessageTimeout: null,
      blockedMessage: "",
    };
  },
  computed: {
    user() {
      const data = localStorage.getItem("user");
      return data ? JSON.parse(data) : null;
    },
    canAccessArchive() {
      return ha(this.user, "archivio_scaricabile_file_convertiti");
    },
    teaserLogs() {
      return [
        {
          id: "preview-1",
          operazione: "Conversione Prospetto Paghe",
          file_name: "Cedolini Marzo 2026.xlsx",
          file_scaricati: ["Prospetti Paghe.zip"],
          uploaded_archived_files: [
            { display_name: "Cedolini Marzo 2026.xlsx" },
          ],
          archived_files: [{ display_name: "Prospetti Paghe.zip" }],
          download_date: "2026-03-28T10:15:00",
        },
        {
          id: "preview-2",
          operazione: "Conversione Modello F24",
          file_name: "F24 Clienti Q1.xlsx",
          file_scaricati: ["Modelli F24.zip"],
          uploaded_archived_files: [{ display_name: "F24 Clienti Q1.xlsx" }],
          archived_files: [{ display_name: "Modelli F24.zip" }],
          download_date: "2026-03-21T15:40:00",
        },
        {
          id: "preview-3",
          operazione: "Conversione Bilancio Analitico",
          file_name: "Bilancio Alfa 2025.xlsx",
          file_scaricati: ["Bilancio Analitico Alfa 2025.xlsx"],
          uploaded_archived_files: [
            { display_name: "Bilancio Alfa 2025.xlsx" },
          ],
          archived_files: [
            { display_name: "Bilancio Analitico Alfa 2025.xlsx" },
          ],
          download_date: "2026-03-11T09:05:00",
        },
      ];
    },
    filteredLogs() {
      const term = this.searchTerm.toLowerCase();
      return this.logs.filter((log) => {
        const operationOk =
          !this.selectedOperation || log.operazione === this.selectedOperation;

        const searchOk =
          !term ||
          (log.file_name || "").toLowerCase().includes(term) ||
          (log.uploaded_archived_files?.[0]?.display_name || "")
            .toLowerCase()
            .includes(term) ||
          (log.archived_files || []).some((file) =>
            (file.display_name || "").toLowerCase().includes(term)
          );

        return operationOk && searchOk;
      });
    },
    displayedLogs() {
      return this.canAccessArchive ? this.filteredLogs : this.teaserLogs;
    },
  },
  mounted() {
    this.fetchArchiveLogs();
  },
  methods: {
    toggleExpand(id) {
      const index = this.expandedRows.indexOf(id);
      if (index === -1) {
        this.expandedRows.push(id);
      } else {
        this.expandedRows.splice(index, 1);
      }
    },
    isExpanded(id) {
      return this.expandedRows.includes(id);
    },
    hasArchivedFiles(log) {
      return (
        Array.isArray(log?.archived_files) && log.archived_files.length > 0
      );
    },
    hasArchivedFileAt(log, index) {
      return !!log?.archived_files?.[index]?.relative_path;
    },
    hasUploadedArchivedFile(log) {
      return !!log?.uploaded_archived_files?.[0]?.relative_path;
    },
    getArchivedFileLabel(log, index) {
      return (
        log?.archived_files?.[index]?.display_name ||
        log?.file_scaricati?.[index] ||
        "Scarica file"
      );
    },
    getUploadedArchivedFileLabel(log) {
      return log?.uploaded_archived_files?.[0]?.display_name || log?.file_name;
    },
    formatDate(isoString) {
      if (!isoString) return "N/D";
      const date = new Date(isoString);
      return date.toLocaleDateString("it-IT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    showArchiveMessage(message) {
      this.archiveMessage = message;
      clearTimeout(this.archiveMessageTimeout);
      this.archiveMessageTimeout = setTimeout(() => {
        this.archiveMessage = "";
      }, 3500);
    },
    handleLockedArchiveClick() {
      if (this.canAccessArchive) return;
      this.showArchiveMessage(
        "Per consultare e riaprire l'archivio file dello studio serve il piano Premium."
      );
    },
    isInlinePreviewable(fileName) {
      const ext = (fileName || "").split(".").pop()?.toLowerCase() || "";
      return [
        "pdf",
        "csv",
        "txt",
        "png",
        "jpg",
        "jpeg",
        "gif",
        "webp",
      ].includes(ext);
    },
    buildArchivedFileUrl(logId, fileIndex, archiveKind, disposition) {
      const params = new URLSearchParams({
        log_id: String(logId),
        file_index: String(fileIndex),
        archive_kind: archiveKind,
        disposition,
      });
      return `${API_BASE_URL}/download_archived_log_file?${params.toString()}`;
    },
    async openOrDownloadArchivedFile(log, fileIndex, archiveKind, fileName) {
      if (!this.canAccessArchive) {
        this.handleLockedArchiveClick();
        return;
      }

      const disposition = this.isInlinePreviewable(fileName)
        ? "inline"
        : "attachment";
      const url = this.buildArchivedFileUrl(
        log.id,
        fileIndex,
        archiveKind,
        disposition
      );

      try {
        if (disposition === "inline") {
          window.open(url, "_blank", "noopener");
          return;
        }

        const response = await axios.get(url, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Errore apertura/download archivio file:", error);
        this.showArchiveMessage(
          "Impossibile aprire o scaricare il file selezionato in questo momento."
        );
      }
    },
    async fetchArchiveLogs() {
      if (!this.canAccessArchive) {
        this.blockedMessage = "";
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      try {
        const response = await axios.get(
          `${API_BASE_URL}/studio/file_archive_logs`
        );
        if (response.data.success) {
          this.logs = response.data.logs || [];
        } else {
          this.logs = [];
        }
      } catch (error) {
        console.error("Errore nel caricamento archivio file:", error);
        this.logs = [];
        this.showArchiveMessage(
          "Impossibile caricare l'archivio file dello studio."
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.main-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100% !important;
}
.main-container.is-embedded {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.is-embedded .container-archivio-file {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  max-width: none;
  height: 100%;
  min-height: 0;
  padding: 0;
  width: 100%;
  overflow: hidden;
}

.container-archivio-file {
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.tool-button {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.title {
  margin: 0;
  text-align: center;
}

.archive-card {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  background: #fff;
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 14px 40px rgba(22, 34, 51, 0.08);
}
.is-embedded .archive-card {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-height: 0;
  padding: 1rem 1.25rem 1.25rem;
  border: 1px solid #dfe7ef;
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(15, 37, 55, 0.07);
}

.premium-chip {
  justify-self: end;
  background: #f7efe0;
  color: #8d5f00;
  padding: 0.34rem 0.72rem;
  border-radius: 999px;
  font-weight: 600;
}

.filters-row {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) auto;
  gap: 0.65rem;
  align-items: center;
  margin: 0 0 0.72rem;
}

.filters-row--locked {
  opacity: 0.72;
}

.filter-input {
  border: 1px solid #d6dfeb;
  border-radius: 8px;
  height: 2.15rem;
  min-height: 2.15rem;
  padding: 0.3rem 0.72rem;
  box-sizing: border-box;
  font: inherit;
}

.filter-input:disabled {
  cursor: not-allowed;
}

.archive-table-shell {
  position: relative;
}
.is-embedded .archive-table-shell {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow: hidden;
}

.archive-table-shell--locked .archive-table-wrap {
  filter: blur(4px);
  opacity: 0.72;
  user-select: none;
}

.premium-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.premium-overlay-card {
  max-width: 460px;
  text-align: center;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(201, 164, 69, 0.28);
  border-radius: 18px;
  padding: 1.25rem 1.35rem;
  box-shadow: 0 18px 38px rgba(22, 34, 51, 0.14);
}

.premium-overlay-card h4 {
  margin: 0 0 0.55rem;
}

.premium-overlay-card p {
  margin: 0;
  color: #607086;
}

.premium-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 0.72rem 1rem;
  border-radius: 12px;
  background: linear-gradient(180deg, #d7a93f, #b98514);
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 24px rgba(166, 118, 14, 0.22);
}

.premium-cta:hover {
  background: linear-gradient(180deg, #c9992e, #a67510);
}

.premium-overlay-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.8rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: #f7efe0;
  color: #8d5f00;
  font-weight: 700;
}

.archive-table {
  width: 100%;
  min-width: 860px;
  border-collapse: separate;
  border-spacing: 0;
}

.archive-table-wrap {
  width: 100%;
  overflow-x: auto;
}
.is-embedded .archive-table-wrap {
  height: 100%;
  min-height: 0;
  overflow: auto;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
}
.is-embedded .archive-table th {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #f3f4f6;
  background-clip: padding-box;
  box-shadow: 0 -1px 0 #f3f4f6, 0 1px 0 #dfe5eb;
}

.archive-table th,
.archive-table td {
  padding: 0.72rem 0.75rem;
  border: 1px solid #e2e6ea;
  text-align: center;
}
.archive-table th {
  color: #13243f;
  font-weight: 700;
}
.archive-table tbody tr:nth-child(odd) td {
  background: #ffffff;
}
.archive-table tbody tr:nth-child(even) td {
  background: #f8f9fa;
}
.archive-table tbody tr.row-expanded td {
  background: #f1f3f5;
}

.archive-table td:nth-child(3) {
  text-align: left;
}

.archive-table td.archive-tool-cell,
.archive-table td.archive-uploaded-cell {
  text-align: left;
}

.archive-table th:nth-child(1),
.archive-table td:nth-child(1) {
  width: 12rem;
  text-align: left;
}

.operationtext,
.expanded-row {
  color: #607086;
}

.operationtext {
  text-align: center;
}

.expanded-row {
  text-align: left !important;
}

.expanded-row .filescaricatititle {
  display: block;
  margin: 0 0 0.65rem;
  color: #4b6584;
  font-weight: 600;
  text-align: left;
}

.expanded-row ul {
  margin: 0;
  padding-left: 1.35rem;
  list-style-position: outside;
  text-align: left;
}

.expanded-row li {
  margin: 0.24rem 0;
  text-align: left;
  line-height: 1.25;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: #eef1f5;
  color: #31455b;
}

.chip i {
  color: #e9aa1d;
}

.clickable-cell {
  cursor: pointer;
}

.archive-link {
  border: 0;
  background: transparent;
  color: #1d4ed8;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: underline;
}

.archive-link.disabled {
  color: #7b8794;
  cursor: not-allowed;
  text-decoration: none;
}

.archive-message,
.blocked-banner {
  margin: 0 0 1rem;
  color: #7c5c00;
}

.blocked-banner {
  background: #fff7e0;
  border: 1px solid #f3df99;
  border-radius: 12px;
  padding: 0.8rem 1rem;
}

.skeleton-loader {
  display: flex;
  width: 60rem;
  max-width: 100%;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
}

.skeleton {
  background-color: #e0e0e0;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}

.skeleton::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  height: 100%;
  width: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

.skeleton-title {
  width: 70%;
  height: 24px;
}

.skeleton-text {
  width: 100%;
  height: 16px;
}

.skeleton-button {
  width: 30%;
  height: 36px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@media (max-width: 768px) {
  .container-archivio-file {
    padding: 1rem;
  }

  .archive-card {
    padding: 1rem;
  }

  .filters-row {
    grid-template-columns: 1fr;
    display: grid;
  }

  .premium-overlay {
    position: static;
    padding: 0 0 1rem;
  }

  .archive-table {
    font-size: 0.92rem;
  }
}
</style>
